<script lang="ts" setup>
import InfoTooltip from '@/core/elements/InfoTooltip.vue';
import { ETooltipBadgeTheme, ETooltipBadgeTone } from '@/interfaces';
import { EPosition } from 'common/enum/misc.enum';
import { computed, useTemplateRef } from 'vue';
import type { VNode } from 'vue';

const slots = defineSlots<{
  'tooltip-content'?: (props: { hide: () => void }) => VNode[];
}>();

const props = withDefaults(
  defineProps<{
    hideLabelWhenTooltip?: boolean;
    label: string | number;
    size?: 'md' | 'xs';
    theme?: ETooltipBadgeTheme;
    tone?: ETooltipBadgeTone;
    tooltipPosition?: EPosition;
    tooltipText?: string;
  }>(),
  {
    hideLabelWhenTooltip: false,
    size: 'md',
    theme: ETooltipBadgeTheme.OUTLINE,
    tone: ETooltipBadgeTone.TINT,
    tooltipPosition: EPosition.BOTTOM_CENTER,
    tooltipText: undefined,
  },
);

const infoTooltipRef = useTemplateRef('infoTooltipRef');

const hasTooltip = computed<boolean>(() => Boolean(props.tooltipText) || slots['tooltip-content'] !== undefined);
const shouldHideLabel = computed<boolean>(() => props.hideLabelWhenTooltip && hasTooltip.value);

const handleBadgeClick = (ev: MouseEvent): void => {
  if (!hasTooltip.value) return;

  infoTooltipRef.value?.show();
  ev.preventDefault();
  ev.stopPropagation();
};

const hideTooltip = (): void => {
  infoTooltipRef.value?.hide();
};
</script>

<template>
  <div
    :class="
      props.theme === ETooltipBadgeTheme.FILLED
        ? [
            'inline-flex items-center',
            'min-w-0 max-w-full',
            'rounded-full md:rounded-8',
            'text-on-tint',
            props.tone === ETooltipBadgeTone.DANGER && 'bg-error',
            props.tone === ETooltipBadgeTone.SECONDARY && 'bg-secondary',
            props.tone === ETooltipBadgeTone.TINT && 'bg-tint',
            shouldHideLabel ? 'p-0' : 'px-[10px]',
            !shouldHideLabel && props.size === 'xs' && 'py-[2px]',
            !shouldHideLabel && props.size === 'md' && 'py-xs',
          ]
        : [
            'flex items-center justify-center',
            shouldHideLabel ? 'p-0' : 'px-smd h-[30px] min-w-10',
            'border-1 border-divider',
            'shrink-0 rounded-badge',
            'font-semibold text-tint',
            'bg-white',
          ]
    "
    @click="handleBadgeClick"
  >
    <span
      :title="`${props.label}`"
      :class="[shouldHideLabel ? 'hidden' : 'truncate', props.size === 'xs' && 'text-md']"
    >
      {{ props.label }}
    </span>
    <InfoTooltip
      v-if="hasTooltip"
      ref="infoTooltipRef"
      :position="props.tooltipPosition"
      :class="props.theme === ETooltipBadgeTheme.FILLED ? 'text-on-tint z-20' : 'z-20'"
      :icon-style="props.theme === ETooltipBadgeTheme.FILLED ? 'text-white w-icon-xs h-icon-xs' : 'w-icon-xs h-icon-xs'"
      content-style="max-w-[380px] whitespace-normal break-words font-normal"
      :button-style="shouldHideLabel ? undefined : 'ml-xs h-icon w-icon-xs shrink-0'"
    >
      <div class="flex flex-col gap-md">
        <span v-if="props.tooltipText" class="whitespace-pre-line">{{ props.tooltipText }}</span>
        <slot v-if="slots['tooltip-content']" name="tooltip-content" :hide="hideTooltip" />
      </div>
    </InfoTooltip>
  </div>
</template>
